<template>
  <v-card flat class="settings-card" v-if="$root.$children[0].isAdmin">
    <v-container>
      <v-form ref="form">
        <v-card-title class="settings-header-text">{{
          title || "Review Settings"
        }}</v-card-title>
        <v-stepper v-model="stepperStep" alt-labels flat>
          <v-stepper-header>
            <v-stepper-step step="1" editable>Request Page</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="2" editable>Feedback Page</v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="3" editable>Thank You Page</v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-row>
                <v-col cols="12">
                  <v-row class="flex" v-if="logoToShow">
                    <v-col col="1" class="flex-shrink-1 flex-grow-0">
                      <v-img
                        v-if="logoToShow"
                        :src="logoUrl"
                        height="60px"
                        width="60px"
                      ></v-img>
                    </v-col>
                    <v-col col="11" class="flex-grow-1">
                      <v-btn small outlined @click="removeReviewLogoURL"
                        >Remove Logo</v-btn
                      >
                    </v-col>
                  </v-row>
                  <v-file-input
                    ref="file_input"
                    v-if="!logoToShow"
                    label="Company Logo"
                    chips
                    accept="image/png, image/jpeg, image/jpg"
                    placeholder="Select your logo"
                    hint="Logo should (60px by 60px)"
                    @change="onLogoSelected"
                    persistent-hint
                    :show-size="1000"
                    required
                    :rules="[rules.required]"
                    outlined
                  >
                  </v-file-input>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="Headline"
                    placeholder="Would you mind sharing your positive feedback?"
                    :value="reviewSettingsConfig['reviewHeadline']"
                    @input="updateConfig('reviewHeadline', $event)"
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    auto-grow
                    rows="1"
                    outlined
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="Subheadline"
                    placeholder="We appreciate serving you and thank you for your business"
                    :value="reviewSettingsConfig['reviewSubheadline']"
                    @input="updateConfig('reviewSubheadline', $event)"
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    auto-grow
                    rows="1"
                    outlined
                  ></v-textarea>
                </v-col>
                <v-col
                  cols="12"
                  class="text-h6 red--text"
                  v-if="reviewSourcesNotConfigured"
                >
                  You must configure
                  {{ reviewSourcesNotConfigured.join(", ") }} from the Review
                  Sources Settings menu.
                </v-col>
                <v-col cols="12">
                  <v-select
                    :value="reviewSettingsConfig['buttons']"
                    @change="updateConfig('buttons', $event)"
                    :items="serviceFeedbackOptions"
                    hint="Click on a service to configure it"
                    persistent-hint
                    item-text="name"
                    item-value="code"
                    label="Select Feedback Options"
                    placeholder="None selected"
                    multiple
                    chips
                    required
                    :rules="[rules.required]"
                    outlined
                  >
                    <template
                      v-slot:selection="{ attrs, item, select, selected }"
                    >
                      <v-chip
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        @click:close="removeButton(item)"
                      >
                        <strong>{{ item.name }}</strong>
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="No Review Solicitation Message"
                    placeholder="You can also see what others are saying about us."
                    :value="reviewSettingsConfig['reviewSubheadlineYelp']"
                    @input="updateConfig('reviewSubheadlineYelp', $event)"
                    hint="Yelp's terms of service prohibits review solicitation, but you can still nudge them there"
                    persistent-hint
                    class="py-0"
                    dense
                    required
                    :rules="[rules.required]"
                    auto-grow
                    rows="1"
                    outlined
                  ></v-textarea>
                </v-col>
                <v-col cols="12" v-if="hasService('Contact')">
                  <v-textarea
                    label="Bad Experience Question"
                    placeholder="Let us know how we can make it right."
                    :value="reviewSettingsConfig['reviewContactMessage']"
                    @input="updateConfig('reviewContactMessage', $event)"
                    hint="You have a chance to make it right privately"
                    persistent-hint
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    auto-grow
                    rows="1"
                    outlined
                  ></v-textarea>
                </v-col>
                <v-col cols="12" v-if="hasService('Contact')">
                  <v-text-field
                    label="Feedback Button Label"
                    placeholder="Contact us directly"
                    :value="reviewSettingsConfig['contactUsFeedbackBtn']"
                    @input="updateConfig('contactUsFeedbackBtn', $event)"
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-card-title>Preview</v-card-title>
                  <v-card-subtitle v-if="placeholderFieldsText">{{
                    placeholderFieldsText
                  }}</v-card-subtitle>
                  <review-feedback-preview
                    pageName="request"
                  ></review-feedback-preview>
                </v-col>
              </v-row>

              <v-dialog v-model="googleDialog" max-width="500px">
                <v-card>
                  <v-card-title>
                    Google Setup
                  </v-card-title>
                  <v-card-text>
                    Business Name
                  </v-card-text>
                  <v-card-actions>
                    <v-btn color="primary" text @click="dialog2 = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Contact Us Headline"
                    placeholder="Your experience was not great?"
                    :value="reviewSettingsConfig['contactUsHeadline']"
                    @input="updateConfig('contactUsHeadline', $event)"
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    label="Contact Us Subheadline"
                    placeholder="Let us know how we can make it right."
                    :value="reviewSettingsConfig['contactUsSubheadline']"
                    @input="updateConfig('contactUsSubheadline', $event)"
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-card-title>Preview</v-card-title>
                  <v-card-subtitle v-if="placeholderFieldsText">{{
                    placeholderFieldsText
                  }}</v-card-subtitle>
                  <review-feedback-preview
                    pageName="feedback"
                  ></review-feedback-preview>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="3">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Feedback Headline"
                    placeholder="Thank you!"
                    :value="
                      reviewSettingsConfig['feedbackConfirmationHeadline']
                    "
                    @input="
                      updateConfig('feedbackConfirmationHeadline', $event)
                    "
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    label="Feedback Subheadline"
                    placeholder="We appreciate your thoughtful feedback."
                    :value="
                      reviewSettingsConfig['feedbackConfirmationSubheadline']
                    "
                    @input="
                      updateConfig('feedbackConfirmationSubheadline', $event)
                    "
                    dense
                    class="py-0"
                    required
                    :rules="[rules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-card-title>Preview</v-card-title>
                  <v-card-subtitle v-if="placeholderFieldsText">{{
                    placeholderFieldsText
                  }}</v-card-subtitle>
                  <review-feedback-preview
                    pageName="thanks"
                  ></review-feedback-preview>
                </v-col>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <v-row class="mt-6 mb-10 float-right">
          <v-btn
            color="primary"
            @click="onSave"
            class="mb-2 px-8"
            :disabled="!(reviewSettings ? reviewSettings.isDirty : false)"
            >Save</v-btn
          >
        </v-row>
      </v-form>
      <v-dialog
        v-model="googleLookupDialog"
        max-width="600"
        style="background:white"
      >
        <google-place-id-lookup-modal></google-place-id-lookup-modal>
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "../../globalVars";
const ReviewFeedbackPreview = () =>
  import(
    /* webpackChunkName: "components-settings-reviews-ReviewFeedbackPreview" */ `./reviews/ReviewFeedbackPreview.vue`
  );
const GooglePlaceIdLookupModal = () =>
  import(
    /* webpackChunkName: "components-settings-modals-GooglePlaceIDLookupModal" */ `./modals/GooglePlaceIDLookupModal.vue`
  );

export default {
  name: "ReviewSettingsPane",
  components: {
    ReviewFeedbackPreview,
    GooglePlaceIdLookupModal
  },
  props: {
    title: String
  },
  data: function() {
    return {
      stepperStep: 1,
      // serviceFeedbackOptions: ["google", "facebook", "yelp", "contact"],
      serviceSelected: "",
      rules: {
        required: value => !!value || "Required"
      },
      googleLookupDialog: false,
      googleDialog: false,
      logoFile: null,
      logoUrl: null
    };
  },
  created() {
    this.$store.dispatch("entities/bizReviewSettings/hydrate");
  },
  computed: {
    account() {
      return this.$store.getters["entities/accounts/query"]()
        .withAllRecursive(1)
        .first();
    },
    userId() {
      return this.$store.getters["entities/settings/query"]().first().userId;
    },
    user() {
      return this.$store.getters["entities/users/find"](this.userId);
    },
    reviewSettings() {
      return this.$store.getters["entities/bizReviewSettings"]().first();
    },
    reviewSettingsConfig() {
      const tempSettings = this.$store.getters[
        "entities/bizReviewSettings"
      ]().first();
      return tempSettings ? tempSettings.config : {};
    },
    bizReviewTypes() {
      let bizReviewTypes = this.$store.getters[
        "entities/bizReviewTypes/query"
      ]().get();
      return bizReviewTypes;
    },
    serviceFeedbackOptions() {
      return [
        ...this.bizReviewTypes,
        {
          code: "contact",
          name: "Contact"
        }
      ];
    },
    googlePlaceId() {
      return this.reviewSettingsConfig["googlePlaceId"];
    },
    facebookPageName() {
      return this.reviewSettingsConfig["facebookPageId"];
    },
    yelpPageName() {
      return this.reviewSettingsConfig["yelpPageId"];
    },
    placeholderFieldsText() {
      let returnValue = "";
      let placeholderFields = [];
      if (!this.reviewSettingsConfig["customerImageSrc"]) {
        placeholderFields.push("logo");
      }
      if (!this.reviewSettingsConfig["reviewStarAverage"]) {
        placeholderFields.push("star rating");
      }
      if (!this.reviewSettingsConfig["reviewStarCount"]) {
        placeholderFields.push("review count");
      }
      if (placeholderFields.length > 0) {
        placeholderFields[placeholderFields.length - 1] =
          "and " + placeholderFields[placeholderFields.length - 1];
        placeholderFields[0] =
          placeholderFields[0].charAt(0).toUpperCase() +
          placeholderFields[0].slice(1);
        if (placeholderFields.length == 2) {
          returnValue = placeholderFields.join(" ");
        } else {
          returnValue = placeholderFields.join(", ");
        }
      }

      return placeholderFields.length > 0
        ? returnValue + " are placeholders until we do the first scan."
        : "";
    },
    logoToShow() {
      if (
        this.reviewSettingsConfig &&
        this.reviewSettingsConfig["customerImageSrc"]
      ) {
        this.updateLogoUrl(this.reviewSettingsConfig["customerImageSrc"]);
        return true;
      }
      return false;
    },
    reviewSourcesNotConfigured() {
      let reviewSourcesNotConfigured = [];
      const buttons = this.reviewSettingsConfig["buttons"].filter(val => {
        return val != "contact";
      });
      const reviewSettingsConfig = this.reviewSettingsConfig || {};
      const reviewTypes = this.bizReviewTypes;
      for (let i = 0; i < buttons.length; i++) {
        const button = buttons[i];
        let reviewType = reviewTypes.find(val => {
          return val.code == button;
        });
        if (
          !(
            reviewType &&
            reviewType.configField &&
            reviewSettingsConfig[reviewType.configField] &&
            reviewSettingsConfig[reviewType.configField].length > 2
          )
        ) {
          if (reviewType && reviewType.name) {
            reviewSourcesNotConfigured.push(reviewType.name);
          }
        }
      }
      return reviewSourcesNotConfigured.length > 0
        ? reviewSourcesNotConfigured
        : null;
    }
  },
  methods: {
    showServiceModal(item) {
      if (item == "google") {
        this.googleDialog = true;
      }
    },
    removeButton(item) {
      this.updateConfig(
        "buttons",
        this.reviewSettingsConfig["buttons"].filter(val => {
          return val != item.code;
        })
      );
    },
    updateLogoUrl(path) {
      if (!path) return;
      let vueThis = this;
      const externalId = this.account.externalId;
      path = path
        .split("/")
        .slice(3)
        .join("/");
      axios({
        method: "get",
        url:
          API_BASE_URL +
          `/account?do=file-url&externalId=${externalId}&path=${path}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      }).then(response => {
        vueThis.logoUrl = response && response.data ? response.data.url : null;
      });
    },
    hasService(service) {
      return (
        this.reviewSettingsConfig["buttons"] &&
        this.reviewSettingsConfig["buttons"].indexOf(service) >= 0
      );
    },
    updateConfig(name, value) {
      let vueThis = this;
      let newConfig = JSON.parse(JSON.stringify(this.reviewSettingsConfig));
      newConfig[name] = value;
      let data = {
        isDirty: true
      };
      data.config = newConfig;
      this.$store.dispatch("entities/bizReviewSettings/update", {
        where: vueThis.reviewSettings.id,
        data: data
      });
    },
    onSave(evt) {
      evt.preventDefault();
      let vueThis = this;
      this.$store.dispatch(
        "entities/bizReviewSettings/updateBizReviewsSettings",
        vueThis.reviewSettings
      );
    },
    openGoogleLookupDialog() {
      this.googleLookupDialog = true;
    },
    onLogoSelected(evt) {
      // get url for upload
      this.logoFile = evt;
      this.onLogoFileUpload();
    },
    onLogoFileUpload() {
      // do the upload
      let vueThis = this;
      let formData = new FormData();
      formData.append("file", this.logoFile);
      axios({
        method: "get",
        url:
          API_BASE_URL +
          `/file-upload/review-logo?name=${encodeURIComponent(
            this.logoFile.name
          )}&type=${encodeURIComponent(this.logoFile.type)}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      })
        .then(response => {
          const filePath = response.data.url.split(".com")[1].split("?")[0];
          axios
            .put(response.data.url, vueThis.logoFile, {
              headers: {
                // "Content-Type": "multipart/form-data"
                "Content-Type": vueThis.logoFile.type
              }
            })
            .then(function() {
              let newConfig = JSON.parse(
                JSON.stringify(vueThis.reviewSettingsConfig)
              );
              newConfig["customerImageSrc"] = "https:/" + filePath;
              let data = {
                isDirty: true,
                config: newConfig
              };
              vueThis.$store.dispatch("entities/bizReviewSettings/update", {
                where: vueThis.reviewSettings.id,
                data: data
              });
              vueThis.$store.dispatch(
                "entities/bizReviewSettings/updateBizReviewsSettings",
                {
                  newConfig
                }
              );
              vueThis.updateLogoUrl(newConfig["customerImageSrc"]);
            })
            .catch(function(error) {
              console.error("Failed to upload file to URL", error);
            });
        })
        .catch(error => {
          console.error("Failed to get an signed upload URL", error);
        });
    },
    removeReviewLogoURL() {
      let newConfig = this.reviewSettingsConfig;
      newConfig["customerImageSrc"] = null;
      let data = {
        isDirty: true,
        config: newConfig
      };
      this.$store.dispatch("entities/bizReviewSettings/update", {
        where: this.reviewSettings.id,
        data: data
      });
    }
  }
};
</script>

<style scoped lang="stylus">
#page-selector {
  text-align: center;
}
.upload-link {
  float: right;
  font-size: .9rem;
  color: #007bff;
  cursor: pointer;
}
.section-side h2 {
  font-size: 2rem;
  margin: 2rem auto 1.5rem;
  text-align: center;
}
.settings-form-reviews {
  padding: 0 1rem;
}
#main {
  width: 100%;
}
@media screen and (min-width: 480px) {
  #main {
    width: 480px;
    margin: 1rem auto 0;
  }
}
#main-content {
  box-sizing: border-box;
  background: #fff;
  width: 100%;
  height: auto;
}
*, *:before, *:after {
  box-sizing: inherit;
}
#main-header {
  box-sizing: border-box;
  background: #1976d2;
  height: 100%;
  width: 100%;
  padding: 20px 40px;
  display: table;
}
#customer-image-box {
  vertical-align: middle;
  padding-right: 15px;
  width: 60px;
  height: 60px;
  display: table-cell;
}
#customer-image-box img {
  width: 60px;
  height: 60px;
}
#customer-info-box {
  vertical-align: middle;
  display: table-cell;
}
#customer-name {
  color: white;
  font-size: 14px;
  margin: -7px 0 7px;
}
#review-stars {
  display: table;
  white-space: nowrap;
}
#review-star-average {
  font-size: 13px;
  color: #fff;
  display: table-cell;
  padding-right: 7px;
}
#review-star-symbols {
  unicode-bidi: bidi-override;
  color: #68b6f3;
  font-size: 13px;
  height: 13px;
  width: 85px;
  margin: 0 auto;
  position: relative;
  padding: 0;
  text-shadow: 0px 1px 0 #68b6f3;
  display: table-cell;
}
#star-ratings-top {
  top: 0px;
  color: #fff;
  padding: 0;
  position: absolute;
  z-index: 1;
  display: block;
  left: 0;
  overflow: hidden;
}
#star-ratings-bottom {
  top: 0px;
  padding: 0;
  display: block;
  z-index: 0;
}
#review-star-symbols span {
  padding: 0 2px;
}
#review-star-count {
  padding-left: 20px;
  font-size: 12px;
  color: #fff;
  display: table-cell;
}
#main-body {
  box-sizing: border-box;
  padding: 40px 30px 60px;
  background: #fff;
  width: 100%;
}
@media screen and (min-width: 480px) {
  #main-body {
    padding: 40px 40px 60px;
  }
}
#main-body-content {
  background: #fff;
}
.review-headline {
  font-size: 28px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #212121;
  font-family: "Roboto Slab",sans-serif;
}
.review-subheadline, #review-subheadline-yelp {
  text-align: center;
  margin: 25px 0;
  font-size: 16px;
  line-height: normal;
  margin-top: -12px;
  font-weight: 300;
  white-space: pre-line;
}
.review-buttons-container {
  margin-top: 40px;
}
button.review-button, button.contact-button, button.feedback-button {
  cursor: pointer;
  width: 280px;
  margin: 10px auto 0;
  height: 52px;
  padding: 2px;
  font-size: 18px;
  font-weight: 400;
  border: 0;
  text-align: center;
  display: table;
  position: relative;
}
@media screen and (min-width: 480px) {
  button.review-button, button.contact-button, button.feedback-button {
    width: 320px;
  }
}
.review-button {
  color: #fff;
  background: #4caf50;
}
button.hider, div.hider, div#review-star-symbols.hider, div#customer-image-box.hider {
  display: none;
}
button.yelp-button {
  background: white;
  border: 1px solid #e53935;
  color: #e53935;
}
div.review-btn-co-icon-container {
  display: inline-block;
  width: 44px;
  height: 45px;
  background: #fff;
  display: table-cell;
}
.review-btn-co-icon {
  display: inline-block;
  vertical-align: middle;
  background: #fff;
  padding: 5px;
  width: 50px;
  height: 50px;
  transform: scale(.7);
  margin: -2px;
  background-image: url(https://static.2rvw.me/images/service-logos.png);
  background-repeat: no-repeat;
}
.review-google-icon {
  background-position: -60px 5px;
}
.review-fb-icon {
  background-position: 5px 5px;
}
.review-yelp-icon {
  background-position: -125px 5px;
}
.review-btn-title {
  display: table-cell;
  width: 100%;
  vertical-align: middle;
  text-align: center;
}
#review-contact-us-container {
  margin: 30px 0 0;
  font-weight: lighter;
}
#review-contact-message {
  color: #212121;
  font-size: 14px;
  font-weight: 300;
  text-align: center;
}
.contact-button {
  color: #fff;
  background: #e53935;
}
#contact-btn-title {
  margin: 0 auto;
}
#review-footer {
  margin: 20px auto;
  color: #999;
  font-size: 12px;
  text-align: center;
  width: 100%;
  padding-bottom: 40px;
}
.review-footer-message {
  font-size: 11px;
  font-weight: 400;
  font-stretch: normal;
  letter-spacing: -.1px;
  color: #999;
  padding-bottom: 10px;
}
.review-footer-message a {
  color: #999;
  text-decoration: none;
}


.textarea-form {
  overflow: auto;
  resize: vertical;
  display: block;
  width: 100%;
  height: 16rem;
  padding: .375rem .75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: .25rem;
  transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
}
#feedback-form div {
  overflow: hidden;
}
#feedback-submit-button {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
  margin-top: 1.5rem;
  width: 100%;
}
</style>
